// A dashboard built on live SharePoint data may only be shown to people who
// can open that workbook themselves. Only Microsoft can answer that, and only
// for someone holding their own delegated token -- which the generated site,
// served as static files, has no way to obtain.
//
// This page is where that happens. The backend bounces an unverified viewer
// here, MSAL hands over a token for them, the backend asks Graph and records
// the answer as a short-lived signed cookie, and the viewer goes straight back
// to the dashboard. The token is never stored on either side.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getGraphFileToken } from '../auth/AuthContext';
import { getApiBaseUrl, authenticatedFetch } from '../utils/api';
import './VerifyDataAccess.css';

// `next` comes back from a redirect, so it is treated as untrusted input: only
// one of KAI's own origins is followed. Without this, a crafted dashboard link
// could use this page as an open redirect to an attacker's site.
//
// Two origins, not one: the dashboard itself is served by the BACKEND, which is
// this same origin behind the /api proxy in production but a separate port in
// development. Comparing against window.location.origin alone rejected every
// real `next` in dev, and the page said "this link is incomplete" for a link
// that was perfectly complete.
const isAllowedNextOrigin = (origin) => {
  if (origin === window.location.origin) return true;
  try {
    return origin === new URL(getApiBaseUrl(), window.location.origin).origin;
  } catch {
    return false;
  }
};

// A ready-to-send request, so asking costs one click rather than a rummage
// through SharePoint for who to contact. Kept short and businesslike -- it
// goes to a colleague who has to action it.
const requestAccessMailto = (ownerEmail, fileName) => {
  const file = fileName || 'the data source';
  const subject = `Access request: ${file}`;
  const body = [
    'Hi,',
    '',
    `I'm trying to open a KAI dashboard that reads live data from ${file}, but I don't currently have permission to view that file in SharePoint.`,
    '',
    'Could you grant me access when you have a moment?',
    '',
    `Dashboard: ${window.location.href}`,
    '',
    'Thank you.',
  ].join('\n');
  return `mailto:${encodeURIComponent(ownerEmail)}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
};

// `next` arrives as a PATH on the backend, never an absolute URL, and this is
// where it becomes one. Only the SPA knows where the backend actually lives:
// production serves it under /api on this same origin, development on a bare
// :8082 with no prefix. The backend cannot tell which -- Apache strips /api
// before the request reaches it -- so sending its own str(request.url) landed
// a viewer whose check had just PASSED on the SPA's catch-all instead of on
// their dashboard, in production only.
//
// Rejecting anything that isn't a plain absolute path is also what makes this
// safe: `//evil.example` is protocol-relative and would otherwise resolve
// off-origin, and a fully-qualified `next` can no longer be supplied at all.
// isAllowedNextOrigin stays as a second line of defence.
const safeNext = (raw) => {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return null;
  try {
    const url = new URL(`${getApiBaseUrl()}${raw}`, window.location.origin);
    return isAllowedNextOrigin(url.origin) ? url.toString() : null;
  } catch {
    return null;
  }
};

const loopKey = (projectId) => `kai-access-check:${projectId}`;
const LOOP_WINDOW_MS = 15000;

// Stamped the moment a full-tab redirect to Microsoft is started, and never
// cleared: the redirect is the last route available, so if it comes back
// without a token, trying it again would only navigate away again.
const redirectKey = (projectId) => `kai-access-redirect:${projectId}`;

const VerifyDataAccess = () => {
  const [state, setState] = useState({ phase: 'checking' });
  // React 18 StrictMode mounts effects twice in development; the check is a
  // network round trip and a redirect, so it must not run twice.
  const startedRef = useRef(false);
  // Where this viewer was going, read once from the URL. Held in a ref because
  // the consent button below re-runs the check after a render.
  const targetRef = useRef({ projectId: null, next: null });
  // Set once the tab is on its way to Microsoft, so the code below doesn't
  // paint an error over a page that is already leaving.
  const redirectStartedRef = useRef(false);

  // `interactive` decides whether MSAL may open a Microsoft consent window.
  // The first pass is always silent; only the button press below sets it, and
  // only because browsers block a popup that no click asked for.
  //
  // Nothing in here may be awaited before getGraphFileToken is CALLED. The
  // popup opens synchronously inside it, and the browser only allows that
  // while the click is still the thing driving the current task.
  const verify = useCallback(async (interactive) => {
    const { projectId, next } = targetRef.current;
    setState({ phase: 'checking' });

    // The full-tab redirect is offered only on the interactive pass and only
    // once per dashboard per session -- see redirectKey.
    let redirectAllowed = false;
    if (interactive) {
      try {
        redirectAllowed = !sessionStorage.getItem(redirectKey(projectId));
      } catch {
        // Private browsing: no guard, so don't offer the route that needs one.
        redirectAllowed = false;
      }
    }
    const onRedirecting = redirectAllowed
      ? () => {
          redirectStartedRef.current = true;
          try {
            sessionStorage.setItem(redirectKey(projectId), '1');
          } catch {
            // Already handled above; this can only fail the same way.
          }
          setState({ phase: 'redirecting' });
        }
      : undefined;

    const token = await getGraphFileToken({ interactive, onRedirecting });
    if (!token) {
      if (redirectStartedRef.current) {
        // The tab is already navigating to Microsoft. Anything painted here
        // would be a flash of the wrong thing on the way out.
        return;
      }
      if (!interactive) {
        // Either nobody has consented to Files.Read.All for this person yet,
        // or the silent iframe can't reach Microsoft because the browser
        // blocks its cookies. Asking out loud fixes both, but it has to come
        // from a click.
        setState({ phase: 'consent' });
        return;
      }
      // They dismissed the window, or the tenant doesn't allow them to
      // consent. Deliberately not a denial: it says nothing about their
      // access, and the backend degrades the same way.
      setState({
        phase: 'error',
        message:
          "KAI couldn't confirm your access to this dashboard's data source. " +
          'If a Microsoft sign-in window opened and was closed, reload this ' +
          'page and try again. Otherwise ask your KAI administrator.',
      });
      return;
    }

    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/websites/${encodeURIComponent(projectId)}/verify-access`,
        { method: 'POST', body: JSON.stringify({ graph_token: token }) }
      );
      // A 5xx is not a denial. Without this, a backend blip parsed to a body
      // with no `status`, fell past the check below and told the viewer they
      // lack SharePoint permission -- sending them to email a colleague about
      // an access problem they never had. The error state says "couldn't
      // check", which is the only thing a failed request actually proves.
      if (!res.ok) {
        throw new Error(`verify-access failed with status ${res.status}`);
      }
      const data = await res.json();

      // Signed into KAI as one person and Microsoft as another. Neither a
      // denial nor a failure -- their access was never actually asked about --
      // so it gets the "couldn't check" framing and advice they can act on,
      // not an email-the-owner button for a file that isn't the problem.
      if (data?.status === 'wrong_account') {
        setState({ phase: 'error', message: data.message });
        return;
      }

      if (data?.status === 'allowed') {
        // Stamped on the way out, not on arrival: a loop is only a loop if the
        // LAST visit succeeded and the dashboard sent them back anyway. Marking
        // every arrival would turn an ordinary reload into a false alarm.
        try {
          sessionStorage.setItem(loopKey(projectId), String(Date.now()));
        } catch {
          // Private browsing: no guard available, which is no worse than before.
        }
        // replace(), not assign(): Back from the dashboard should return to
        // wherever they came from, not bounce through this check again.
        window.location.replace(next);
        return;
      }
      setState({
        phase: 'denied',
        message:
          data?.message ||
          "This dashboard reads live data from a SharePoint file your account " +
            "doesn't currently have permission to open. Please request access " +
            "from the file's owner, then reload this page to continue.",
        fileName: data?.file_name || '',
        ownerEmail: data?.owner_email || '',
      });
    } catch (err) {
      setState({
        phase: 'error',
        message: "KAI couldn't confirm your access right now. Try reloading this page.",
      });
    }
  }, []);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const projectId = params.get('project_id');
    const next = safeNext(params.get('next'));
    targetRef.current = { projectId, next };

    // If the grant cookie doesn't stick -- blocked cookies, a clock far out of
    // sync -- the dashboard bounces straight back here and the viewer spins
    // between two pages forever. The stamp is written only when a check has
    // just PASSED (see verify above), so landing here again within seconds of
    // one means the grant didn't survive the trip.
    // Timestamped, not a flag: a flag cleared on success is no guard at all
    // (the loop only happens AFTER a success), and a flag never cleared would
    // break the legitimate re-check once the grant expires. Coming back in 30
    // minutes is just the TTL doing its job.
    let loopedBack = false;
    try {
      const last = Number(sessionStorage.getItem(loopKey(projectId)) || 0);
      loopedBack = last > 0 && Date.now() - last < LOOP_WINDOW_MS;
      sessionStorage.removeItem(loopKey(projectId));
    } catch {
      // Private browsing: no guard available, which is no worse than before.
    }
    if (loopedBack) {
      setState({
        phase: 'error',
        message:
          "KAI confirmed your access but the dashboard keeps asking again. This is " +
          'usually browser cookie settings blocking KAI. Try a normal (non-private) ' +
          'window, or allow cookies for this site.',
      });
      return;
    }

    if (!projectId || !next) {
      setState({ phase: 'error', message: 'This link is incomplete. Ask for the dashboard link again.' });
      return;
    }

    verify(false);
  }, [verify]);

  if (state.phase === 'checking' || state.phase === 'redirecting') {
    return (
      <div className="verify-access-page">
        <div className="verify-access-card">
          <div className="verify-access-spinner" aria-hidden="true" />
          <p>
            {state.phase === 'redirecting'
              ? 'Taking you to Microsoft to confirm your access…'
              : "Checking your access to this dashboard’s data…"}
          </p>
        </div>
      </div>
    );
  }

  if (state.phase === 'consent') {
    return (
      <div className="verify-access-page">
        <div className="verify-access-card">
          <h1>One quick check</h1>
          <p>
            This dashboard reads live data from SharePoint. KAI needs your
            permission to ask Microsoft whether you can open that file.
          </p>
          <button type="button" className="verify-access-button" onClick={() => verify(true)}>
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Heading deliberately frames the next step rather than the refusal. "You
  // don't have access" reads as an accusation for what is almost always an
  // ordinary situation: the dashboard was shared more widely than the
  // workbook behind it.
  const denied = state.phase === 'denied';
  return (
    <div className="verify-access-page">
      <div className="verify-access-card">
        <h1>{denied ? 'Request access to continue' : "Couldn't check your access"}</h1>
        <p>{state.message}</p>
        {denied && state.ownerEmail && (
          <a
            className="verify-access-button"
            href={requestAccessMailto(state.ownerEmail, state.fileName)}
          >
            Email {state.ownerEmail}
          </a>
        )}
      </div>
    </div>
  );
};

export default VerifyDataAccess;
